import { HttpClient } from '@angular/common/http';
import { DestroyRef, inject, Service, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { environment } from '@env/environment';
import { ErrorDialog } from '@shared/components/error-dialog/error-dialog';
import { IApiResponse } from '@shared/models/api-response';
import { IAsset, ICurrencies } from '@shared/models/asset.model';
import { IGeneratedAddress } from '@shared/models/generated-address.model';
import { ITerminalInfo } from '@shared/models/terminal-info.model';
import { Observable, take } from 'rxjs';

@Service()
export class Pay {
  private http = inject(HttpClient);
  token = '';
  hashToken = '';
  assets = signal<ICurrencies[]>([]);
  terminalInfo = signal<ITerminalInfo | null>(null);
  private destroyRef = inject(DestroyRef);

  private dialogService: MatDialog = inject(MatDialog);
  errorDialogIsOpen = false;

  getJWTToken(token: string) {
    return new Promise<any>((resolve, reject) => {
      this.http
        .get<IApiResponse<{ jwtToken: string }>>(`${environment.api}/ipg/b2p/crypto/${token}/get-jwt-token`)
        .pipe(take(1))
        .subscribe({
          next: (res) => {
            this.token = res.data.jwtToken;
            resolve(res.data.jwtToken);
          },
          error: (err) => {
            this.showErrorDialog();
            resolve(null);
          },
        });
    });
  }

  getAssets() {
    this.http
      .get<IApiResponse<IAsset>>(`${environment.api}/ipg/b2p/crypto/assets`)
      .pipe(take(1))
      .subscribe({
        next: (res) => {
          this.assets.set(res.data.currencies);
        },
        error: (err) => {
          this.assets.set([]);
          this.showErrorDialog();
        },
      });
  }

  generateAddress(currencyId: number, networkId: number): Observable<IApiResponse<IGeneratedAddress>> {
    return this.http.put<IApiResponse<IGeneratedAddress>>(`${environment.api}/ipg/b2p/crypto/address`, {
      data: {
        currencyId: currencyId,
        networkId: networkId,
      },
    });
  }

  getTerminalInfo() {
    this.http
      .get<IApiResponse<ITerminalInfo>>(`${environment.api}/ipg/b2p/crypto/base-info`)
      .pipe(take(1))
      .subscribe({
        next: (res) => {
          this.terminalInfo.set(res.data);
        },
        error: (err) => {
          this.terminalInfo.set(null);
          this.showErrorDialog();
        },
      });
  }

  backToBroker() {
    window.location.href = this.terminalInfo()?.returnUrl ?? environment.returnUrl;
  }

  showErrorDialog() {
    if (this.errorDialogIsOpen) {
      return;
    }

    this.errorDialogIsOpen = true;
    this.dialogService
      .open(ErrorDialog, {
        disableClose: true,
        backdropClass: 'custom-dialog-backdrop',
        data: 'This operation is not available at the moment. Please contact your administrator.',
        width: '460px',
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        this.errorDialogIsOpen = false;
        if (result) {
          window.location.reload();
        }
      });
  }
}
